// src/pages/Login.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { BASE_URL, firebaseConfig } from "shared/lib/config";
import logo from "assets/images/potentialogo.png";
import computerkids2 from "assets/images/computerkids_small.png";

// import deusfinalogo from "../assets/images/deus.jpg";

/* ----------------------------- asset caching ----------------------------- */
const CACHE_KEY = "potentia-assets";
async function cacheImage(url) {
  if ("caches" in window) {
    try {
      const cache = await caches.open(CACHE_KEY);
      const hit = await cache.match(url);
      if (!hit) await cache.add(url);
    } catch (err) {
      // best effort only
      console.debug("[cacheImage] skip:", err);
    }
  }
}

/* --------------------------- remember-me helpers ------------------------- */
const LAST_EMAIL_KEY = "lastLoginEmail";
const LAST_PW_KEY = "lastLoginPw_b64"; // convenience (not secure)

function saveLastCreds(email, password, remember) {
  try {
    if (remember) {
      if (email) localStorage.setItem(LAST_EMAIL_KEY, email);
      if (typeof password === "string")
        localStorage.setItem(LAST_PW_KEY, btoa(password));
    } else {
      localStorage.removeItem(LAST_EMAIL_KEY);
      localStorage.removeItem(LAST_PW_KEY);
    }
  } catch {}
}

function loadLastCreds() {
  try {
    const email = localStorage.getItem(LAST_EMAIL_KEY) || "";
    const pw64 = localStorage.getItem(LAST_PW_KEY) || "";
    return { email, password: pw64 ? atob(pw64) : "" };
  } catch {
    return { email: "", password: "" };
  }
}

/* ------------------------ instant-load prewarm helpers ------------------------ */
// Minimal class builder (mirrors Teacher.jsx logic)
function buildTeacherClassesFromAssessments(assessments = []) {
  const counts = new Map(); // classId -> Set<unique test_taker_name>
  const info = new Map();   // classId -> { id, name, academicYear, slug }

  for (const a of assessments) {
    const clsObj = a?.classId && typeof a.classId === "object" ? a.classId : null;
    const classId = clsObj?._id || (typeof a?.classId === "string" ? a.classId : null);
    if (!classId) continue;

    if (!info.has(classId)) {
      info.set(classId, {
        id: classId,
        name: clsObj?.name || "—",
        academicYear: clsObj?.academicYear || null,
        slug: clsObj?.slug || null,
      });
    }

    const taker = (a?.test_taker_name || "").trim();
    if (!counts.has(classId)) counts.set(classId, new Set());
    if (taker) counts.get(classId).add(taker);
  }

  const out = [];
  for (const [classId, set] of counts.entries()) {
    const meta = info.get(classId);
    out.push({
      id: classId,
      name: meta?.name || "—",
      academicYear: meta?.academicYear || null,
      studentsCount: set.size,
      slug: meta?.slug || null,
    });
  }
  return out.sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

// Build classes using API-provided classes if present; otherwise fallback
function buildTeacherClassesFromPayload(payload) {
  const apiClasses = Array.isArray(payload?.classes) ? payload.classes : null;
  const assessments = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
    ? payload
    : [];

  if (apiClasses && apiClasses.length) {
    return apiClasses
      .map((c) => ({
        id: c.id || c._id,
        slug: c.slug || null,
        name: c.name || "—",
        academicYear: c.academicYear || null,
        studentsCount: c.studentsCount ?? 0,
      }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }
  return buildTeacherClassesFromAssessments(assessments);
}

/**
 * Prefetch /api/assessments and cache results so Teacher/Parents pages render instantly.
 * - For teacher/principal: sets localStorage.teacherClassesCache
 * - For others: sets localStorage.reportAssessmentsCache
 */
async function prewarmAfterLogin({ token, role, pushLog }) {
  try {
    pushLog?.({ step: "prewarm.fetch.start" });
    const res = await fetch(`${BASE_URL}/api/assessments`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payloadText = await res.text();
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${payloadText.slice(0, 500)}`);

    const payload = JSON.parse(payloadText || "{}");

    if (role === "teacher" || role === "principal") {
      const classes = buildTeacherClassesFromPayload(payload);
      localStorage.setItem("teacherClassesCache", JSON.stringify(classes));
      // Optional: also keep raw payload for future flows
      localStorage.setItem("teacherAssessmentsRaw", payloadText);
      pushLog?.({ step: "prewarm.teacher.cached", classesCount: classes.length });
    } else {
      // Parents / students / general users: cache assessments for report/parents dashboards
      const rows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];
      localStorage.setItem("reportAssessmentsCache", JSON.stringify(rows));
      pushLog?.({ step: "prewarm.report.cached", rows: rows.length });
    }
  } catch (err) {
    // best-effort only
    pushLog?.({ step: "prewarm.error", error: String(err) });
  }
}

/* -------------------------------- component ------------------------------ */
export default function Login() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const debugEnabled = new URLSearchParams(search).get("debug") === "1";

  // Firebase (init once)
  const app = useMemo(() => initializeApp(firebaseConfig), []);
  const auth = useMemo(() => getAuth(app), [app]);

  // Refs to inputs
  const emailRef = useRef(null);
  const passRef = useRef(null);

  // UI state
  const [toast, setToast] = useState({ open: false, msg: "", stage: "" });
  const [emailErr, setEmailErr] = useState("");
  const [passErr, setPassErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [debugOpen, setDebugOpen] = useState(debugEnabled);
  const [debugLogs, setDebugLogs] = useState([]);

  // mode: login / reset
  const [mode, setMode] = useState("login");
  const [resetEmail, setResetEmail] = useState("");
  const [resetErr, setResetErr] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSentTo, setResetSentTo] = useState("");

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]{2,}$/.test(email);
  const year = new Date().getFullYear();

  /* --------------------------- first-load bootstrap --------------------------- */
  useEffect(() => {
    // cache the left illustration (faster second load)
    cacheImage(computerkids2);

    // prefill from prior "Remember me"
    const { email, password } = loadLastCreds();
    if (emailRef.current) emailRef.current.value = email || "";
    if (passRef.current) passRef.current.value = password || "";

    // if we had saved creds, keep the box checked
    // if (email || password) setRemember(true);
  }, []);

  /* --------------------------------- helpers -------------------------------- */
  const pushLog = (entry) => {
    const e = { ts: new Date().toISOString(), ...entry };
    setDebugLogs((prev) => [...prev, e]);
    // eslint-disable-next-line no-console
    console.debug("[LoginDebug]", e);
  };

  const showToast = (msg, stage = "") => {
    setToast({ open: true, msg, stage });
    setTimeout(() => setToast({ open: false, msg: "", stage: "" }), 4200);
  };

  const clearStorage = () => {
    ["idToken", "userProfile"].forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
  };

  const firebaseMsg = (code) =>
    ({
      "auth/invalid-email": "Format Email Invalid",
      "auth/user-disabled": "Akun ini telah dinonaktifkan.",
      "auth/user-not-found": "Tidak ada akun dengan email tersebut",
      "auth/wrong-password": "Password atau email yang salah",
      "auth/invalid-credential": "Email atau password yang salah",
      "auth/too-many-requests":
        "Terlalu banyak percobaan. Silahkan mencoba lagi nanti.",
      "auth/network-request-failed":
        "Network error reaching Firebase. Check your connection.",
    }[code] || `Firebase error (${code || "unknown"})`);

  /* ------------------------------ auto-login ------------------------------ */
  useEffect(() => {
    (async () => {
      const token =
        sessionStorage.getItem("idToken") || localStorage.getItem("idToken");
      if (!token) return; // no token => don't auto-login

      try {
        pushLog({ step: "autologin.start" });
        const res = await fetch(`${BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const bodyText = await res.text();
        pushLog({
          step: "autologin.profile.fetch.done",
          status: res.status,
          body: bodyText.slice(0, 500),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${bodyText}`);
        const profile = JSON.parse(bodyText || "{}");

        // cache profile where it's already stored:
        const useSession = !!sessionStorage.getItem("idToken");
        const store = useSession ? sessionStorage : localStorage;
        store.setItem("userProfile", JSON.stringify(profile));

        // 🔥 Prewarm data so first visit to Teacher/Parents is instant
        await prewarmAfterLogin({ token, role: profile.role, pushLog });

      if (profile.role === "admin") {
      navigate("/admin", { replace: true });
    } else if (
      profile.role === "teacher" ||
      profile.role === "principal"
    ) {
      navigate("/teacher", { replace: true });
    } else if (profile.role === "financeAdmin") {
      navigate("/finance", { replace: true });
    } else {
      navigate("/hasil_asesmen", { replace: true });
    }

      
      } catch (err) {
        pushLog({ step: "autologin.failed", error: String(err) });
        clearStorage();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------------------------- submit -------------------------------- */
  const onSubmit = async (e) => {
    e.preventDefault();
    setEmailErr("");
    setPassErr("");
    setLoading(true);

    const email = emailRef.current.value.trim();
    const password = passRef.current.value;

    let hasError = false;
    if (!email) {
      setEmailErr("Email diperlukan");
      hasError = true;
    } else if (!isValidEmail(email)) {
      setEmailErr("Tolong masukkan alamat email yang valid.");
      hasError = true;
    }
    if (!password) {
      setPassErr("Password diperlukan.");
      hasError = true;
    }
    if (hasError) {
      setLoading(false);
      return;
    }

    try {
      pushLog({ step: "signin.firebase.start", email });
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const token = await cred.user.getIdToken(true);
      pushLog({ step: "signin.firebase.success", uid: cred.user.uid });

      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), 15000);

      pushLog({ step: "signin.backend.fetch.start", url: "/api/auth/me" });
      const res = await fetch(`${BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: ac.signal,
      });
      clearTimeout(timer);

      const bodyText = await res.text();
      pushLog({
        step: "signin.backend.fetch.done",
        status: res.status,
        body: bodyText.slice(0, 800),
      });

      if (!res.ok) {
        let backendMsg = bodyText;
        try {
          backendMsg = JSON.parse(bodyText)?.message || backendMsg;
        } catch {}
        throw new Error(
          `Backend profile fetch failed (HTTP ${res.status}) — ${backendMsg}`
        );
      }

      const profile = JSON.parse(bodyText || "{}");
      const store = remember ? localStorage : sessionStorage;
      store.setItem("idToken", token);
      store.setItem("userProfile", JSON.stringify(profile));

      // persist/clear last creds per remember state
      saveLastCreds(email, password, remember);

      // 🔥 Prewarm fetch for instant first render after login
      await prewarmAfterLogin({ token, role: profile.role, pushLog });

          if (profile.role === "admin") {
      navigate("/admin", { replace: true });
    } else if (profile.role === "teacher" || profile.role === "principal") {
      navigate("/teacher", { replace: true });
    } else if (profile.role === "financeAdmin") {
      navigate("/finance", { replace: true });
    } else {
      navigate("/hasil_asesmen", { replace: true });
    }

      } catch (e2) {
      const code = e2?.code || "";
      const isFirebase = code?.startsWith?.("auth/");
      const stage = isFirebase ? "Firebase sign-in" : "Backend /api/auth/me";
      const msg = isFirebase ? firebaseMsg(code) : String(e2.message || e2);

      if (code === "auth/invalid-email" || code === "auth/user-not-found") {
        setEmailErr("Tolong cek email anda.");
      }
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setPassErr("Email atau kata sandi salah.");
      }

      pushLog({
        step: "signin.failed",
        stage,
        code,
        error: String(e2),
        stack: e2?.stack?.split("\n").slice(0, 3).join(" | "),
      });

      const isCredentialError =
        code === "auth/wrong-password" ||
        code === "auth/invalid-credential" ||
        code === "auth/user-not-found" ||
        code === "auth/invalid-email";

      if (!isCredentialError) {
        showToast(`Sign-in failed at: ${stage}. ${msg}`, "Firebase sign-in");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------- reset flow ------------------------------ */
  const openReset = () => {
    setResetErr("");
    setResetSentTo("");
    const currentEmail = emailRef.current?.value?.trim() || "";
    setResetEmail(currentEmail);
    setMode("reset");
  };
  const backToLogin = () => {
    setMode("login");
    setResetLoading(false);
    setResetErr("");
    setResetSentTo("");
  };
  const submitReset = async (e) => {
    e.preventDefault();
    setResetErr("");
    setResetLoading(true);
    const email = (resetEmail || "").trim();
    if (!email) {
      setResetErr("Email diperlukan");
      setResetLoading(false);
      return;
    }
    if (!isValidEmail(email)) {
      setResetErr("Tolong masukkan email yang valid!");
      setResetLoading(false);
      return;
    }
    try {
      pushLog({ step: "passwordreset.start", email });
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin + "/",
        handleCodeInApp: false,
      });
      pushLog({ step: "passwordreset.sent", email });
      setResetSentTo(email);
    } catch (err) {
      pushLog({ step: "passwordreset.error", error: String(err) });
      setResetSentTo(email);
    } finally {
      setResetLoading(false);
    }
  };

  /* ---------------------------------- UI ---------------------------------- */
  return (
    <div className="min-h-dvh bg-gradient-to-br from-purple-50 via-orange-50 to-teal-50 relative overflow-hidden">
      {/* background blobs (subtle on mobile) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-16 left-4 md:left-20 w-44 md:w-72 h-44 md:h-72 bg-purple-200 rounded-full mix-blend-multiply blur-xl opacity-60 md:opacity-70 animate-blob" />
        <div className="absolute top-36 right-6 md:right-20 w-44 md:w-72 h-44 md:h-72 bg-orange-200 rounded-full mix-blend-multiply blur-xl opacity-60 md:opacity-70 animate-blob [animation-delay:2s]" />
        <div className="absolute bottom-16 left-16 md:left-40 w-44 md:w-72 h-44 md:h-72 bg-teal-200 rounded-full mix-blend-multiply blur-xl opacity-60 md:opacity-70 animate-blob [animation-delay:4s]" />
      </div>

      <div className="relative z-10 min-h-dvh flex items-center justify-center px-4 py-6 md:py-10">
        <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center">
          {/* left visual (hidden on small for speed/clarity) */}
          <div className="hidden lg:flex flex-col items-center justify-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-400 to-purple-500 rounded-3xl p-3 md:p-4 shadow-2xl rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="bg-white rounded-2xl p-3 md:p-4 shadow-lg">
                  <img
                    src={computerkids2}
                    alt="Kids learning on a computer"
                    loading="lazy"
                    decoding="async"
                    width="380"
                    height="285"
                    className="block w-[320px] md:w-[380px] h-auto rounded-xl shadow"
                  />
                </div>
              </div>
              <div className="absolute -bottom-5 -right-5 bg-gradient-to-r from-orange-400 to-pink-500 text-white px-4 py-2 rounded-full shadow-lg rotate-12 hover:rotate-6 transition-transform duration-300">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l1.5 3L14 9l-3.5 1-1.5 3-1.5-3L4 9l3.5-1L9 5zM18 13l.75 1.5L20 15l-1.25.5L18 17l-.75-1.5L16 15l1.25-.5L18 13z"
                    />
                  </svg>
                  Eksplor
                </div>
              </div>
            </div>

            <div className="mt-8 text-center max-w-xl">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 md:mb-3">
                Maksimalkan Potensi Belajar Anak
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                Temukan wawasan tentang perjalanan belajar anak Anda melalui
                asesmen menyeluruh dan laporan yang dipersonalisasi.
              </p>
            </div>
          </div>

          {/* right card */}
          <div className="w-full max-w-md mx-auto">
            <div
              className="relative bg-white rounded-2xl md:rounded-3xl shadow-xl md:shadow-2xl p-6 md:p-8 lg:p-10 border border-gray-100"
              id="card"
            >
              {/* toast (only on login mode) */}
              {mode === "login" && (
                <div
                  className={`pointer-events-auto absolute left-1/2 -translate-x-1/2 top-3 md:top-4 w-[92%] max-w-sm
                    ${toast.stage ? "bg-red-50" : "bg-gray-50"}
                    text-red-700 border ${
                      toast.stage ? "border-red-200" : "border-gray-200"
                    } rounded-lg px-3 py-2 text-xs md:text-sm
                    transition-all duration-200 ${
                      toast.open
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-95"
                    }`}
                  role="status"
                  aria-live="polite"
                >
                  <strong>{toast.stage ? `${toast.stage}: ` : ""}</strong>
                  {toast.msg}
                </div>
              )}

              {/* header logos */}
              <div className="text-center mb-6 md:mb-8">
                <div className="inline-flex items-center justify-center gap-3 mb-3 md:mb-4">
                  {/* <img
                    src={deusfinalogo}
                    alt="Deus Logo"
                    className="h-9 md:h-10 w-auto mx-auto drop-shadow-md"
                  /> */}
                  {/* <span className="h-5 md:h-6 w-px bg-gray-300" /> */}
                  <img
                    src={logo}
                    alt="Potentia Logo"
                    className="h-11 md:h-14 w-auto mx-auto drop-shadow-md"
                  />
                </div>
                <h1 className="text-xl md:text-3xl font-bold text-gray-800 mb-1 md:mb-2">
                  {mode === "login"
                    ? "Selamat Datang di Potentia"
                    : "Atur ulang kata sandi Anda"}
                </h1>
                <p className="text-gray-600 text-sm md:text-base">
                  {mode === "login"
                    ? "Ketahui potensi dan gaya belajar anak Anda. Silahkan masuk untuk melihat hasil asesmen."
                    : "Masukkan email Anda dan kami akan mengirimkan tautan aman untuk mengatur ulang kata sandi Anda."}
                </p>
              </div>

              {/* LOGIN FORM */}
              {mode === "login" && (
                <form className="space-y-5 md:space-y-6" onSubmit={onSubmit} noValidate>
                  {/* email */}
                  <div>
                    <label
                      className="block text-sm font-semibold text-gray-700 mb-2"
                      htmlFor="email"
                    >
                      Email
                    </label>
                    <div className="relative">
                      <input
                        id="email"
                        ref={emailRef}
                        type="email"
                        inputMode="email"
                        placeholder="anda@contoh.com"
                        autoComplete="username"
                        autoCorrect="off"
                        spellCheck="false"
                        onInput={() => setEmailErr("")}
                        className={`w-full px-4 py-3.5 md:py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-gray-800
                          ${
                            emailErr
                              ? "border-red-300 focus:ring-red-500"
                              : "border-gray-200 focus:ring-blue-500 focus:border-transparent"
                          }`}
                      />
                    </div>
                    <p
                      className={`min-h-4 mt-1 text-xs text-red-600 ${
                        emailErr ? "" : "invisible"
                      }`}
                    >
                      {emailErr || " "}
                    </p>
                  </div>

                  {/* password */}
                  <div>
                    <label
                      className="block text-sm font-semibold text-gray-700 mb-2"
                      htmlFor="password"
                    >
                      Kata Sandi
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        ref={passRef}
                        type={showPw ? "text" : "password"}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        onInput={() => setPassErr("")}
                        className={`w-full px-4 pr-16 py-3.5 md:py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-gray-800
                          ${
                            passErr
                              ? "border-red-300 focus:ring-red-500"
                              : "border-gray-200 focus:ring-blue-500 focus:border-transparent"
                          }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-600 transition-colors p-1 rounded-md"
                        aria-label={showPw ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
                      >
                        {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <p
                      className={`min-h-4 mt-1 text-xs text-red-600 ${
                        passErr ? "" : "invisible"
                      }`}
                    >
                      {passErr || " "}
                    </p>
                  </div>

                  {/* remember + forgot (larger touch targets) */}
                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center gap-3 cursor-pointer -mx-2 px-2 py-1.5 rounded-md hover:bg-gray-50">
                      <input
                        type="checkbox"
                        className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-all"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        aria-label="Ingat saya"
                      />
                      <span className="text-gray-700 font-medium select-none">
                        Ingat saya
                      </span>
                    </label>
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800 font-medium px-2 py-1 -mx-2"
                      onClick={openReset}
                    >
                      Lupa Kata Sandi?
                    </button>
                  </div>

                  {/* submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 md:hover:from-blue-700 md:hover:to-purple-700 text-white font-bold py-3.5 md:py-3 rounded-xl transition-all duration-200 active:scale-[0.98] shadow-lg md:hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {!loading ? (
                      <span>Masuk</span>
                    ) : (
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white spinner rounded-full animate-spin" />
                        Masuk…
                      </span>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center mt-4 leading-relaxed">
                    Dengan melanjutkan, anda menyetujui{" "}
                    <button className="text-gray-400 cursor-not-allowed" disabled>
                      ketentuan
                    </button>{" "}
                    dan{" "}
                    <button className="text-gray-400 cursor-not-allowed" disabled>
                      kebijakan kerahasiaan pribadi
                    </button>
                    .
                  </p>

                  {/* Contact Support line */}
                  <p className="text-sm text-center mt-3 md:mt-4 text-gray-600">
                    Perlu bantuan?{" "}
                    <Link
                      to="/contact"
                      className="text-blue-600 hover:text-blue-800 font-medium underline-offset-2 hover:underline"
                    >
                      Hubungi kami
                    </Link>
                  </p>
                </form>
              )}

              {/* RESET FORM */}
              {mode === "reset" && (
                <form className="space-y-5 md:space-y-6" onSubmit={submitReset} noValidate>
                  <div>
                    <label
                      className="block text-sm font-semibold text-gray-700 mb-2"
                      htmlFor="reset-email"
                    >
                      Email
                    </label>
                    <input
                      id="reset-email"
                      type="email"
                      inputMode="email"
                      value={resetEmail}
                      onChange={(e) => {
                        setResetEmail(e.target.value);
                        setResetErr("");
                      }}
                      placeholder="you@example.com"
                      className={`w-full px-4 py-3.5 md:py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-gray-800
                        ${
                          resetErr
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-200 focus:ring-blue-500 focus:border-transparent"
                        }`}
                    />
                    <p
                      className={`min-h-4 mt-1 text-xs text-red-600 ${
                        resetErr ? "" : "invisible"
                      }`}
                    >
                      {resetErr || " "}
                    </p>
                  </div>

                  {resetSentTo && (
                    <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      Jika akun dengan email ini ada untuk:{" "}
                      <strong>{resetSentTo}</strong>, tautan pengaturan ulang
                      kata sandi telah dikirim. Silahkan periksa email dan folder spam Anda.
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={backToLogin}
                      className="text-sm text-gray-600 hover:text-gray-800 px-2 py-1 -mx-2"
                    >
                      ← Kembali
                    </button>

                    <button
                      type="submit"
                      disabled={resetLoading}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 md:hover:from-blue-700 md:hover:to-purple-700 text-white font-bold px-5 py-2.5 rounded-xl shadow-lg md:hover:shadow-xl disabled:opacity-70"
                    >
                      {resetLoading ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white spinner rounded-full animate-spin" />
                          Mengirim…
                        </>
                      ) : (
                        "Mengirimkan tautan set ulang"
                      )}
                    </button>
                  </div>
                </form>
              )}

              {/* Debug panel (optional)
              <DebugPanel
                className="mt-6"
                open={debugOpen}
                onToggle={() => setDebugOpen((v) => !v)}
                logs={debugLogs}
              /> */}
            </div>
          </div>
        </div>
      </div>

      <footer className="relative z-10 w-full text-center py-5 md:py-6 text-gray-500 text-xs md:text-sm">
        © <span>{year}</span> Deus Human Capital Services | Potentia
      </footer>
    </div>
  );
}
